import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import type { SidebarMenuItem } from './sidebar-types';
import { cn } from '@/lib/cn';

interface MenuItemProps {
  paddingY: string;
  isOpened: boolean;
  setIsOpened?: Dispatch<SetStateAction<boolean>>;
  isOpenItem: boolean;
  toggleOpenItem: () => void;
  isSearchUsed?: boolean;
  search?: string;
  item: SidebarMenuItem;
}

function resolveMatch(label: string, search: string) {
  const regExp = new RegExp(search, 'gi');
  const matches = [...label.matchAll(regExp)];
  const parts: [number, number, boolean][] = matches.reduce(
    (acc, curr) => {
      if (typeof curr.index !== 'number') return acc;

      const prev = acc.length && acc.at(-1);
      if (!prev && curr.index) acc.push([0, curr.index, false]);
      if (prev && prev[1] < curr.index) acc.push([prev[1], curr.index, false]);

      return [...acc, [curr.index, curr.index + curr[0].length, true]];
    },
    [] as [number, number, boolean][],
  );

  const lastItem = parts.length && parts.at(-1);
  if (lastItem && lastItem[1] < label.length) {
    parts.push([lastItem[1], label.length, false]);
  }
  return parts;
}

export const MenuItem = ({
  item,
  search,
  paddingY,
  isOpened,
  isOpenItem,
  toggleOpenItem,
  isSearchUsed,
  setIsOpened,
}: MenuItemProps) => {
  const {
    disabled,
    subItems,
    subSections,
    labelClassName,
    label,
    disabledHint,
    disabledIcon: DisabledIcon,
    icon: Icon,
    labelAction,
    activeIcon: ActiveIcon,
    notification,
    pathname,
    opener,
    onClick,
    tooltipHelpText,
  } = item;
  const hasNestedItems = !!((subItems && subItems.length) || (subSections && subSections.length));

  const isActive = window.location.pathname === pathname;

  const resolveLabel = useMemo(() => {
    if (!search || typeof label !== 'string') return label;

    const items = resolveMatch(label, search);
    if (!items.length) return label;

    return (
      <span>
        {items.map(([start, end, isMatch], i) => (
          <span key={i} className={cn({ 'text-white': isMatch })}>
            {label.substring(start, end)}
          </span>
        ))}
      </span>
    );
  }, [label, search]);

  const RenderIcon = disabled ? DisabledIcon : isActive ? ActiveIcon || Icon : Icon;

  return (
    <div
      title={tooltipHelpText || ''}
      onClick={
        disabled
          ? undefined
          : e => {
              e.stopPropagation();
              if (opener && hasNestedItems) toggleOpenItem();
              if (!opener && setIsOpened && !hasNestedItems) setIsOpened(false);
              if (!isActive || hasNestedItems) {
                onClick?.();
              }
            }
      }
      className={cn('w-full flex items-center px-4 select-none', {
        'cursor-pointer hover:bg-white hover:bg-opacity-[5%] border-l-2 border-l-transparent':
          !disabled && !isActive,
        'bg-white bg-opacity-[7%] border-l-2 border-l-[#0EA5E9]': isActive && !disabled,
      })}
      style={{
        paddingTop: paddingY,
        paddingBottom: paddingY,
      }}
    >
      {RenderIcon ? (
        <RenderIcon
          className={cn('mr-3 h-6 w-6 min-w-6 min-h-6 shrink-0', {
            'text-gray-600': disabled,
            'text-white': isActive && !disabled,
            'text-gray-400': !isActive && !disabled,
          })}
        />
      ) : null}
      {isOpened && (
        <>
          <span
            className={cn('text-[20px] leading-[22px] sm:text-[14px]', labelClassName, {
              'text-white': isActive && !disabled,
              'text-gray-400': !isActive && !disabled,
              'text-gray-600': disabled,
            })}
          >
            <div className="truncate">{resolveLabel}</div>
            {labelAction}
          </span>
          {hasNestedItems ? (
            <span className="ml-auto flex items-center gap-2">
              {isSearchUsed && (
                <span className="text-sm opacity-64">
                  {subItems?.length ?? subSections?.length ?? 0}
                </span>
              )}
              <span
                className="cursor-pointer select-none rounded-full p-[2px] hover:bg-gray-700"
                onClick={
                  hasNestedItems
                    ? e => {
                        e.stopPropagation();
                        toggleOpenItem();
                      }
                    : undefined
                }
              >
                {isOpenItem ? (
                  <ChevronDown className="h-5 w-5 sm:h-5 sm:w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5 sm:h-5 sm:w-5" />
                )}
              </span>
            </span>
          ) : (
            <>
              {disabled && (
                <span className="ml-auto select-none" title={disabledHint || ''}>
                  <Lock className="h-5 w-5 min-h-[20px] min-w-[20px]" />
                </span>
              )}
              {notification && (
                <div className="ml-auto mt-[2px] h-2 w-2 select-none rounded-full bg-red-600" />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
